import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Box, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Document, Page, pdfjs } from "react-pdf";
import {
  FaArrowLeft,
  FaArrowRight,
  FaEdit,
  FaSearch,
  FaSearchMinus,
  FaSearchPlus,
} from "react-icons/fa";
import FormPageHeader from "../admin/quizSystem/components/FormPageHeader";
import {
  deleteEmployeePdf,
  fetchEmployeePdfs,
  uploadEmployeePdfs,
  updateEmployeePdf,
} from "../store/reducers/employeePdfSlice";
import {
  setEmployeePdfAddModal,
  setEmployeePdfEditModal,
  setSelectedEmployeePdf,
} from "../store/reducers/modalSlice";
import EmployeePdfAddModal from "./employee-pdfs/components/EmployeePdfAddModal";
import EmployeePdfEditModal from "./employee-pdfs/components/EmployeePdfEditModal";
import { getPdfName, getPdfUrl } from "./employee-pdfs/utils/pdfHelpers";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const screenSectionSx = {
  backgroundColor: "#fff",
  border: "1px solid #eef2f7",
  borderRadius: "10px",
  boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
  overflow: "hidden",
};

const formPanelStyle = {
  background: "#fff",
  border: "1px solid #eef2f7",
  borderRadius: "10px",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
  padding: "16px",
};

const useEmployeePdfAccess = () => {
  const organizationId = localStorage.getItem("organisation_id");
  const role = localStorage.getItem("user_role");
  const { organizations } = useSelector((state) => state.userOrganizations);
  const { organizationDetails } = useSelector((state) => state.organization);
  const canAccessAdminOwnerModulesForRole = (value) => {
    const normalizedRole = `${value || ""}`
      .toLowerCase()
      .replace(/[\s_-]/g, "");

    return (
      normalizedRole === "admin" ||
      normalizedRole === "owner" ||
      normalizedRole === "superadmin"
    );
  };

  const isAdminOwnerFromList = organizations.some((org) => {
    return (
      org.organisation_id === +organizationId &&
      canAccessAdminOwnerModulesForRole(org.role)
    );
  });

  return {
    organizationId,
    featureEnabled: organizationDetails?.view_employee_pdfs,
    isAdminOwner: isAdminOwnerFromList || canAccessAdminOwnerModulesForRole(role),
  };
};

// ─────────────────────────────────────────────
// Viewer (employee-facing)
// ─────────────────────────────────────────────
const EmployeePdfViewer = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const viewerRef = useRef(null);

  const { organizationId, featureEnabled } = useEmployeePdfAccess();
  const { pdfs } = useSelector((state) => state.employeePdfs);

  const [selectedPdfId, setSelectedPdfId] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [zoom, setZoom] = useState(isMobile ? 0.92 : 1.2);
  const [fileBlob, setFileBlob] = useState(null);
  const [viewerWidth, setViewerWidth] = useState(0);

  const pdfList = useMemo(() => (Array.isArray(pdfs) ? pdfs : []), [pdfs]);

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchEmployeePdfs(organizationId));
    }
  }, [dispatch, organizationId]);

  useEffect(() => {
    if (!pdfList.length) {
      setSelectedPdfId(null);
      return;
    }
    const activePdf = pdfList.find((item) => item.id === selectedPdfId);
    if (!activePdf) {
      setSelectedPdfId(pdfList[0].id);
    }
  }, [pdfList, selectedPdfId]);

  const selectedIndex = useMemo(
    () => pdfList.findIndex((item) => item.id === selectedPdfId),
    [pdfList, selectedPdfId]
  );

  const selectedPdf = useMemo(
    () => (selectedIndex >= 0 ? pdfList[selectedIndex] : pdfList[0] || null),
    [pdfList, selectedIndex]
  );

  const rawUrl = getPdfUrl(selectedPdf);

  useEffect(() => {
    setNumPages(null);
    if (!rawUrl) {
      setFileBlob(null);
      return;
    }

    let objectUrl;
    const loadPdf = async () => {
      try {
        const res = await fetch(rawUrl, { cache: "no-store" });
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        setFileBlob(objectUrl);
      } catch (err) {
        console.error("PDF load error", err);
        setFileBlob(null);
      }
    };

    loadPdf();
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [rawUrl]);

  useEffect(() => {
    const blockViewerEvent = (event) => {
      if (!viewerRef.current?.contains(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
    };

    const blockShortcuts = (event) => {
      const isMetaPressed = event.ctrlKey || event.metaKey;
      const blockedKey = event.key?.toLowerCase();
      if (isMetaPressed && ["c", "s", "p"].includes(blockedKey)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener("contextmenu", blockViewerEvent, true);
    document.addEventListener("copy", blockViewerEvent, true);
    document.addEventListener("cut", blockViewerEvent, true);
    document.addEventListener("dragstart", blockViewerEvent, true);
    document.addEventListener("selectstart", blockViewerEvent, true);
    window.addEventListener("keydown", blockShortcuts, true);

    return () => {
      document.removeEventListener("contextmenu", blockViewerEvent, true);
      document.removeEventListener("copy", blockViewerEvent, true);
      document.removeEventListener("cut", blockViewerEvent, true);
      document.removeEventListener("dragstart", blockViewerEvent, true);
      document.removeEventListener("selectstart", blockViewerEvent, true);
      window.removeEventListener("keydown", blockShortcuts, true);
    };
  }, []);

  const selectPdf = (index) => {
    const nextPdf = pdfList[index];
    if (!nextPdf) return;
    setSelectedPdfId(nextPdf.id);
    setZoom(isMobile ? 0.92 : 1.2);
  };

  useEffect(() => {
    const viewerElement = viewerRef.current;

    if (!viewerElement) return undefined;

    let animationFrameId = null;

    const updateViewerWidth = () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        const nextWidth = viewerElement.clientWidth;
        setViewerWidth((currentWidth) =>
          currentWidth === nextWidth ? currentWidth : nextWidth
        );
      });
    };

    updateViewerWidth();

    const resizeObserver = new ResizeObserver(updateViewerWidth);
    resizeObserver.observe(viewerElement);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      resizeObserver.disconnect();
    };
  }, [isMobile, fileBlob]);

  const zoomIn = () =>
    setZoom((prev) => Math.min(prev + 0.2, isMobile ? 1.8 : 3));
  const zoomOut = () =>
    setZoom((prev) => Math.max(prev - 0.2, isMobile ? 0.7 : 0.6));
  const onDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages);

  const handleNextPdf = () => {
    if (selectedIndex < pdfList.length - 1) {
      selectPdf(selectedIndex + 1);
    }
  };

  const handlePrevPdf = () => {
    if (selectedIndex > 0) {
      selectPdf(selectedIndex - 1);
    }
  };

  if (featureEnabled === false) {
    return (
      <div className="p-3 text-center">
        Organisation does not have permission
      </div>
    );
  }

  return (
    <div className="min-h-[93vh] overflow-auto bg-white p-3 text-[#20232A] sm:p-4">
      <FormPageHeader title={t("userMenuSidebar.label.EmployeePdfs")} />

      <Box
        sx={{
          ...screenSectionSx,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          height: isMobile ? "auto" : "calc(93vh - 72px)",
          minHeight: isMobile ? "100dvh" : undefined,
        }}
      >
        {/* ── Sidebar (desktop) ── */}
        {!isMobile && (
          <Box
            sx={{
              width: "28%",
              overflowY: "auto",
              borderRight: "1px solid #ddd",
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              PDF List
            </Typography>

            {pdfList.length ? (
              pdfList.map((item, index) => (
                <Paper
                  key={item.id}
                  onClick={() => selectPdf(index)}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    cursor: "pointer",
                    backgroundColor:
                      index === selectedIndex
                        ? "var(--org-bg-tint-1, #ffebee)"
                        : "#fff",
                    border:
                      index === selectedIndex
                        ? "1px solid #ef5350"
                        : "1px solid #eee",
                  }}
                >
                  <Typography variant="body2">
                    <strong>{getPdfName(item)}</strong>
                  </Typography>
                </Paper>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No PDFs found.
              </Typography>
            )}
          </Box>
        )}

        {/* ── Main content ── */}
        <Box
          sx={{
            width: isMobile ? "100%" : "72%",
            p: isMobile ? 1 : 2,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {/* Mobile: dropdown selector */}
          {isMobile && (
            <Box sx={{ mb: 1 }}>
              <select
                className="form-select"
                value={selectedIndex >= 0 ? selectedIndex : ""}
                onChange={(e) => selectPdf(Number(e.target.value))}
                disabled={!pdfList.length}
              >
                {!pdfList.length && <option value="">No PDFs available</option>}
                {pdfList.map((item, index) => (
                  <option key={item.id} value={index}>
                    {getPdfName(item)}
                  </option>
                ))}
              </select>
            </Box>
          )}

          {/* Toolbar */}
          <Box
            sx={{
              mb: 1,
              p: 1,
              borderRadius: "6px",
              backgroundColor: "#f5f5f5",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{
                fontSize: isMobile ? "0.85rem" : undefined,
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedPdf ? getPdfName(selectedPdf) : "No PDF selected"}
            </Typography>
          </Box>

          {/* PDF canvas */}
          <Box
            ref={viewerRef}
            sx={{
              flex: 1,
              overflow: "auto",
              border: "1px solid #ccc",
              borderRadius: "8px",
              p: isMobile ? 1 : 2,
              backgroundColor: "#fafafa",
              position: "relative",
              userSelect: "none",
              WebkitUserSelect: "none",
              WebkitTouchCallout: "none",
              minHeight: isMobile ? "60vh" : undefined,
              "& canvas": {
                userSelect: "none",
                WebkitUserDrag: "none",
                maxWidth: "100%",
                height: "auto !important",
              },
              "& img": {
                pointerEvents: "none",
                userSelect: "none",
                WebkitUserDrag: "none",
              },
            }}
          >
            {fileBlob ? (
              <Document
                key={fileBlob}
                file={fileBlob}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={
                  <span className="spinner-border spinner-border-sm"></span>
                }
              >
                {Array.from(new Array(numPages), (_, i) => (
                  <Box key={i} mb={2} display="flex" justifyContent="center">
                    <Page
                      pageNumber={i + 1}
                      scale={isMobile ? undefined : zoom}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      width={
                        isMobile && viewerWidth
                          ? Math.max(220, Math.floor((viewerWidth - 16) * zoom))
                          : undefined
                      }
                    />
                  </Box>
                ))}
              </Document>
            ) : (
              <Typography align="center" mt={5}>
                {pdfList.length ? "No PDF available" : "No PDFs available"}
              </Typography>
            )}

            {/* Zoom Controls — sticky bottom-right overlay inside viewer */}
            <Box
              sx={{
                position: "sticky",
                bottom: isMobile ? 8 : 12,
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                pointerEvents: "none",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "row" : "column",
                  gap: 1,
                  pointerEvents: "auto",
                }}
              >
                <button
                  className="btn btn-light btn-sm shadow-sm"
                  onClick={zoomIn}
                  title="Zoom In"
                  aria-label="Zoom in"
                  style={{
                    width: 36,
                    height: 36,
                    padding: 0,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaSearchPlus size={14} />
                </button>
                <button
                  className="btn btn-light btn-sm shadow-sm"
                  onClick={zoomOut}
                  title="Zoom Out"
                  aria-label="Zoom out"
                  style={{
                    width: 36,
                    height: 36,
                    padding: 0,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaSearchMinus size={14} />
                </button>
              </Box>
            </Box>
          </Box>

          {/* Navigation Bar */}
          <Box
            sx={{
              mt: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: isMobile ? "center" : "flex-end",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <button
              className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
              onClick={handlePrevPdf}
              disabled={selectedIndex <= 0}
              style={{
                width: 34,
                height: 34,
                padding: 0,
                borderRadius: "50%",
              }}
            >
              <FaArrowLeft size={13} />
            </button>

            <Typography
              variant="body2"
              sx={{
                minWidth: 90,
                textAlign: "center",
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              PDF <strong>{selectedIndex >= 0 ? selectedIndex + 1 : 1}</strong>
              {" of "}
              <strong>{pdfList.length}</strong>
            </Typography>

            <button
              className="btn btn-primary btn-sm d-flex align-items-center justify-content-center"
              onClick={handleNextPdf}
              disabled={
                selectedIndex < 0 || selectedIndex === pdfList.length - 1
              }
              style={{
                width: 34,
                height: 34,
                padding: 0,
                borderRadius: "50%",
              }}
            >
              <FaArrowRight size={13} />
            </button>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

// ─────────────────────────────────────────────
// Delete Confirmation Modal
// ─────────────────────────────────────────────
const DeletePdfModal = ({ show, onHide, onConfirm, deleting }) => (
  <Modal show={show} onHide={onHide} centered size="sm">
    <Modal.Body style={{ padding: "2rem 1.5rem 1.5rem", textAlign: "center" }}>
      {/* Icon */}
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "#FEE2E2",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 1.1rem",
        }}
      >
        <i
          className="fas fa-trash-alt"
          style={{ fontSize: 22, color: "#DC2626" }}
        />
      </div>

      {/* Heading */}
      <h6
        style={{
          fontWeight: 700,
          fontSize: 16,
          color: "#111827",
          marginBottom: 8,
        }}
      >
        Delete PDF?
      </h6>

      {/* Description */}
      <p
        style={{
          fontSize: 13,
          color: "#6B7280",
          marginBottom: "1.5rem",
          lineHeight: 1.5,
        }}
      >
        This PDF will be permanently removed and employees will no longer be
        able to access it. This action cannot be undone.
      </p>

      {/* Actions */}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={onHide}
          disabled={deleting}
          style={{
            flex: 1,
            padding: "8px 0",
            borderRadius: 8,
            border: "1px solid #E5E7EB",
            background: "#fff",
            color: "#374151",
            fontWeight: 500,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={deleting}
          style={{
            flex: 1,
            padding: "8px 0",
            borderRadius: 8,
            border: "none",
            background: deleting ? "#FCA5A5" : "#DC2626",
            color: "#fff",
            fontWeight: 600,
            fontSize: 13,
            cursor: deleting ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            transition: "background 0.15s",
          }}
        >
          {deleting ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                style={{ width: 12, height: 12, borderWidth: 2 }}
              />
              Deleting…
            </>
          ) : (
            <>
              <i className="fas fa-trash-alt" style={{ fontSize: 12 }} />
              Yes, Delete
            </>
          )}
        </button>
      </div>
    </Modal.Body>
  </Modal>
);

// ─────────────────────────────────────────────
// Management (admin-facing)
// ─────────────────────────────────────────────
export const EmployeePdfManagement = () => {
  const dispatch = useDispatch();
  const { organizationId, featureEnabled, isAdminOwner } =
    useEmployeePdfAccess();
  const { pdfs, loading, saving, deleting, total } = useSelector(
    (state) => state.employeePdfs
  );
  const { employeePdfAddModal, employeePdfEditModal, selectedEmployeePdf } =
    useSelector((state) => state.modal);

  const itemsPerPage = 10;

  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const pdfList = useMemo(() => (Array.isArray(pdfs) ? pdfs : []), [pdfs]);

  useEffect(() => {
    if (organizationId && featureEnabled && isAdminOwner) {
      dispatch(
        fetchEmployeePdfs({
          organizationId,
          management: true,
          page: currentPage,
          limit: itemsPerPage,
          search: debouncedSearch,
        })
      );
    }
  }, [
    currentPage,
    debouncedSearch,
    dispatch,
    featureEnabled,
    isAdminOwner,
    organizationId,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  if (featureEnabled === false || !isAdminOwner) {
    return (
      <div className="p-3 text-center">
        Organisation does not have permission
      </div>
    );
  }

  const refreshPdfList = (page = currentPage) =>
    dispatch(
      fetchEmployeePdfs({
        organizationId,
        management: true,
        page,
        limit: itemsPerPage,
        search: debouncedSearch,
      })
    );

  const openAddModal = () => {
    dispatch(setEmployeePdfAddModal(true));
  };

  const closeAddModal = () => {
    dispatch(setEmployeePdfAddModal(false));
  };

  const openEditModal = (pdf) => {
    dispatch(setSelectedEmployeePdf(pdf));
    dispatch(setEmployeePdfEditModal(true));
  };

  const closeEditModal = () => {
    dispatch(setEmployeePdfEditModal(false));
    dispatch(setSelectedEmployeePdf(null));
  };

  const handleSavePdf = async ({ name, files }) => {
    const formData = new FormData();
    formData.append("organization_id", organizationId);
    formData.append("name", name);
    files.forEach((file) => {
      formData.append("pdf_files", file);
    });

    try {
      await dispatch(uploadEmployeePdfs(formData)).unwrap();
      toast.success(
        files.length > 1
          ? "PDFs uploaded successfully"
          : "PDF uploaded successfully"
      );
      closeAddModal();
      setCurrentPage(1);
      refreshPdfList(1);
      return true;
    } catch (err) {
      toast.error(err?.error || "Upload failed");
      return false;
    }
  };

  const handleUpdatePdf = async ({ name, file, fileUrl, pdf }) => {
    if (!pdf?.id) return false;

    const payload = new FormData();
    payload.append("organization_id", organizationId);
    payload.append("name", name);
    payload.append("pdf_id", pdf.id);
    payload.append("pdf_file", file || fileUrl);

    try {
      await dispatch(
        updateEmployeePdf({
          data: payload,
          isFormData: true,
        })
      ).unwrap();
      toast.success("PDF updated successfully");
      closeEditModal();
      refreshPdfList();
      return { success: true };
    } catch (err) {
      const message =
        err?.error || err?.message || err?.pdf_file?.[0] || "Update failed";
      toast.error(message);
      return {
        success: false,
        error: message,
      };
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(
        deleteEmployeePdf({ id: selectedDeleteId, organizationId })
      ).unwrap();
      toast.success("PDF deleted successfully");
      setShowDeleteModal(false);
      dispatch(
        fetchEmployeePdfs({
          organizationId,
          management: true,
          page: currentPage,
          limit: itemsPerPage,
          search: debouncedSearch,
        })
      );
    } catch (error) {
      toast.error(error?.error || "Delete failed");
    }
  };

  const pageCount = Math.ceil((Number(total) || 0) / itemsPerPage);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected + 1);
  };

  return (
    <>
      <div style={{ marginTop: 12 }}>
        <FormPageHeader title="General Instruction PDFs" />
      </div>
      <div style={formPanelStyle}>
        <div className="row mb-3 align-items-center">
          <div className="col-12 d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div
              className="d-flex align-items-center"
              style={{
                width: "min(100%, 320px)",
                border: "1px solid #ccc",
                borderRadius: "6px",
                backgroundColor: "#fff",
                overflow: "hidden",
              }}
            >
              <FaSearch
                size={14}
                style={{ color: "#6b7280", marginLeft: 12, flexShrink: 0 }}
              />
              <input
                type="text"
                className="form-control border-0 shadow-none"
                placeholder="Search PDF"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ backgroundColor: "transparent" }}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setDebouncedSearch("");
                    setCurrentPage(1);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    padding: "0 10px",
                    cursor: "pointer",
                    color: "#999",
                    fontSize: "16px",
                    lineHeight: 1,
                  }}
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>

            <button
              className="btn"
              type="button"
              onClick={openAddModal}
              style={{
                whiteSpace: "nowrap",
                backgroundColor: "#e06060",
                color: "#fff",
                borderRadius: "6px",
                border: "none",
                padding: "6px 16px",
                fontWeight: 500,
                fontSize: "0.875rem",
              }}
            >
              Add new PDF
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th style={{ width: "80px" }}>Sr. No</th>
                <th style={{ textAlign: "center" }}>PDF Name</th>
                <th style={{ width: "80px", textAlign: "center" }}>Edit</th>
                <th style={{ width: "80px", textAlign: "center" }}>Remove</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="text-center py-3">
                    Loading...
                  </td>
                </tr>
              ) : pdfList.length ? (
                pdfList.map((item, i) => (
                  <tr key={item.id}>
                    <td>{i + 1 + itemsPerPage * (currentPage - 1)}</td>
                    <td
                      style={{
                        textAlign: "center",
                        maxWidth: "200px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {getPdfName(item)}
                    </td>
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn btn-sm bg_color"
                        onClick={() => openEditModal(item)}
                        aria-label={`Edit ${getPdfName(item)}`}
                        title="Edit PDF"
                      >
                        <FaEdit size={13} />
                      </button>
                    </td>
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn btn-sm bg_color"
                        onClick={() => handleDeleteClick(item.id)}
                        aria-label="Delete PDF"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-3">
                    Data Not Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {pageCount > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <ReactPaginate
              nextLabel="Next"
              previousLabel="Previous"
              onPageChange={handlePageClick}
              pageCount={pageCount}
              forcePage={currentPage - 1}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              breakLabel="..."
              breakClassName="page-item"
              breakLinkClassName="page-link"
              containerClassName="pagination flex-wrap"
              activeClassName="active"
            />
          </div>
        )}

        <EmployeePdfAddModal
          show={employeePdfAddModal}
          onHide={closeAddModal}
          onSave={handleSavePdf}
          saving={!!saving}
        />

        <EmployeePdfEditModal
          show={employeePdfEditModal}
          onHide={closeEditModal}
          onSave={handleUpdatePdf}
          saving={!!saving}
          pdf={selectedEmployeePdf}
        />

        {/* ── Delete Confirmation Modal ── */}
        <DeletePdfModal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          deleting={!!deleting}
        />
      </div>
    </>
  );
};

export default EmployeePdfViewer;
